import { useState, useEffect, useRef } from "react";
import { useSostaStyles } from "./useSostaStyles";
import Footer from "./Footer";

// ── Data ──────────────────────────────────────────────────────────────────────
const FEATURES = [
  { icon: "💬", title: "Sosta Chat", desc: "Your AI BFF who pulls from your actual history to remind you exactly why you don't need to text him." },
  { icon: "📅", title: "No contact tracker", desc: "Track your days no contact and days since the breakup. Watch your streak grow. Stay accountable." },
  { icon: "🎥", title: "Glow up videos", desc: "Scroll real advice from girls who've been through it. Glow up tips, breakup stories, moving on content." },
  { icon: "🫂", title: "Community", desc: "A safe space to vent, share, and connect with women going through the exact same thing as you." },
];

const SCREENSHOTS = [
  { src: "screenshot1.png", alt: "Sosta splash screen" },
  { src: "screenshot2.png", alt: "Sosta chat" },
  { src: "screenshot3.png", alt: "Sosta community" },
  { src: "screenshot4.png", alt: "Sosta profile" },
];

const CHAT = [
  { role: "user", text: "I miss him so much. I keep wanting to text him 😭", ts: "read 11:47 PM" },
  { role: "bot",  text: "Do you miss the flowers he never gave you? Or the dates he never planned???", ts: "11:47 PM" },
  { role: "user", text: "lol ok fair... but I still wanna text him", ts: "11:48 PM" },
  { role: "bot",  text: "You realize if he wanted to text you he could. Soooo he's basically choosing NOT to. You shouldn't text him either, babe.", ts: "11:48 PM" },
  { role: "user", text: "what if he just wants me to make the first move?", ts: "11:49 PM" },
  { role: "bot",  text: "Oh I'm sorry… is he the girl in this scenario? Do you want a girlfriend or a man? You choose. 💅", ts: "11:49 PM" },
];

const MAILCHIMP_URL = "https://sosta.us3.list-manage.com/subscribe/post?u=6ae58118c2ace41a1a9845192&id=243a95728f&f_id=00bc25e2f0";
const MAILCHIMP_HP  = "b_6ae58118c2ace41a1a9845192_243a95728f";

// ── Helpers ───────────────────────────────────────────────────────────────────
function submitToMailchimp(email) {
  const form = document.createElement("form");
  form.action = MAILCHIMP_URL;
  form.method = "POST";
  form.target = "_blank";
  form.style.display = "none";
  const e = document.createElement("input"); e.name = "EMAIL"; e.value = email;
  const hp = document.createElement("input"); hp.name = MAILCHIMP_HP; hp.value = "";
  hp.tabIndex = -1; hp.style.cssText = "position:absolute;left:-5000px";
  form.append(e, hp);
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
}

function useInView(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.12 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return visible;
}

// ── EmailForm ─────────────────────────────────────────────────────────────────
function EmailForm({ inputId, btnClass, inputClass, formClass }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(false);

  function submit() {
    if (!email || !email.includes("@")) { setErr(true); setTimeout(() => setErr(false), 1500); return; }
    submitToMailchimp(email);
    setDone(true);
  }

  if (done) return <p className="s-success">You're in! Welcome to Sosta 🩷</p>;

  return (
    <div className={formClass}>
      <input
        id={inputId} type="email" placeholder="your@email.com"
        className={inputClass}
        value={email} onChange={e => setEmail(e.target.value)}
        style={err ? { borderColor: "#e24b4a" } : {}}
        onKeyDown={e => e.key === "Enter" && submit()}
      />
      <button className={btnClass} onClick={submit}>Download now</button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function SostaLanding() {
  const [scrolled, setScrolled] = useState(false);

  const featRef    = useRef(null);
  const trackerRef = useRef(null);
  const featVisible    = useInView(featRef);
  const trackerVisible = useInView(trackerRef);

  useSostaStyles();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* Nav */}
      <nav className={`s-nav${scrolled ? " scrolled" : ""}`}>
        <div className="s-logo">Sosta<span className="s-logo-dot" /></div>
        <a
          href="https://apps.apple.com/app/sosta"
          target="_blank" rel="noreferrer"
          className="s-nav-btn"
        >Download now</a>
      </nav>

      {/* Hero */}
      <section className="s-hero">
        <div className="s-badge">Now live on the App Store — download FREE today</div>
        <p className="s-tagline">for the girls, by the girls 💅</p>
        <h1 className="s-h1">Your brutally honest<br /><em>breakup bestie.</em></h1>
        <p className="s-hero-sub">
          Sosta helps you stop spiraling, stay no contact, and actually get over him — with a bff who tells you the truth, not what you want to hear.
          <br /><br />
          Made with 🩷 for every girl who needs this... by a girl who needed this.
        </p>
        <EmailForm
          inputId="hero-email"
          formClass="s-form"
          inputClass=""
          btnClass="s-btn"
        />
        <div className="s-social">
          <div className="s-avatars">
            {["S","M","J","A"].map((l, i) => (
              <div key={i} className="s-avatar" style={{ background: ["#D4537E","#8B4F6B","#F4A7C0","#c27a96"][i], color: i === 2 ? "#8B4F6B" : "white" }}>{l}</div>
            ))}
          </div>
          <span>Join 200+ women already breaking the cycle</span>
        </div>
      </section>

      {/* Features */}
      <section className="s-features" ref={featRef}>
        <p className="s-label">Everything you need</p>
        <h2 className="s-title">Built for the hard nights.</h2>
        <div className="s-grid">
          {FEATURES.map((f, i) => (
            <div key={i} className={`s-card${featVisible ? " visible" : ""}`} style={{ animationDelay: `${i * 0.1}s` }}>
              <div className="s-card-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Screenshots */}
      <section className="s-shots">
        <p className="s-label">See it for yourself</p>
        <h2 className="s-title">Everything she needs.<br />All in one place.</h2>
        <div className="s-shots-row">
          {SCREENSHOTS.map((s, i) => (
            <div key={i} className="s-shot">
              <img src={s.src} alt={s.alt} loading="lazy" />
            </div>
          ))}
        </div>
      </section>

      {/* Chat preview */}
      <section className="s-chat">
        <div className="s-chat-inner">
          <p className="s-label">See it in action</p>
          <h2 className="s-title">She doesn't sugarcoat it.</h2>
          <div className="s-chat-window">
            <div className="s-chat-header">
              <div className="s-chat-name">🩷 Sosta</div>
            </div>
            {CHAT.map((m, i) => (
              <div key={i} className={`s-msg ${m.role}`}>
                <div className="s-bubble">{m.text}</div>
                <div className="s-ts">{m.ts}</div>
              </div>
            ))}
            <div className="s-msg bot">
              <div className="s-bubble">
                <span className="s-dot" /><span className="s-dot" /><span className="s-dot" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tracker */}
      <section className="s-tracker" ref={trackerRef}>
        <p className="s-label">Your healing, tracked</p>
        <h2 className="s-title">Every day counts.</h2>
        <p className="s-tracker-desc">Watch your no-contact streak grow. Celebrate the hard days. Your profile tracks every milestone so you never forget how far you've come.</p>
        <div className="s-tracker-cards">
          {[["28","Days since breakup"],["14","Days no contact"]].map(([n,l],i) => (
            <div key={i} className={`s-tcard${trackerVisible ? " visible" : ""}`} style={{ animationDelay: `${i * 0.15}s` }}>
              <div className="s-tnum">{n}</div>
              <div className="s-tlabel">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="s-cta">
        <h2>Ready to get a grip?</h2>
        <p className="s-cta-sub">Download now and let us know your thoughts... we're still in beta so every bit of feedback helps!</p>
        <EmailForm
          inputId="cta-email"
          formClass="s-cta-form"
          inputClass=""
          btnClass="s-btn-white"
        />
      </section>

      <Footer />
    </>
  );
}
